import React, { useState } from 'react'
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Button,
} from '@material-ui/core'
import { EditableTextField, EditableSwitch, EditableSelect } from '../common/EditableComponents'
import { utahIcsEnvironmentOptions } from '../../utils/constants'

const UtahIcsFacilities = ({
  data,
  shops = [],
  onChangeFacility,
  onAddFacility,
  onRemoveFacility,
  onSaveFacility,
}) => {
  const account = data.utahIcsAccount || {}
  const facilities = account.facilities || []
  const availableShops = shops.filter(
    (shop) => !facilities.some((facility) => facility.shopId === shop.id),
  )
  const [editingIds, setEditingIds] = useState(new Set())

  const toggleEdit = (facilityId, enable) => {
    setEditingIds((prev) => {
      const next = new Set(prev)
      if (enable) {
        next.add(facilityId)
      } else {
        next.delete(facilityId)
      }
      return next
    })
  }

  const isEditing = (facilityId) => editingIds.has(facilityId)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className="paper-page">
          <div className="table-scroll">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: 150 }}>Shop</TableCell>
                  <TableCell style={{ minWidth: 130 }}>Environment</TableCell>
                  <TableCell style={{ minWidth: 140 }}>Facility Code</TableCell>
                  <TableCell style={{ minWidth: 140 }}>Client ID</TableCell>
                  <TableCell style={{ minWidth: 140 }}>Client Secret</TableCell>
                  <TableCell style={{ minWidth: 90 }}>Enabled</TableCell>
                  <TableCell style={{ minWidth: 110 }}>Enable Sync</TableCell>
                  <TableCell style={{ minWidth: 180 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facilities &&
                  facilities.map((facility, index) => {
                    const facilityId = facility.id || facility._id || index
                    const selectedShop = shops.find((shop) => shop.id === facility.shopId) || {}
                    const rowEditMode = isEditing(facilityId)
                    return (
                      <TableRow key={facilityId}>
                        <TableCell className="cell-wrap-word">{selectedShop.name || ''}</TableCell>
                        <TableCell className="cell-wrap-word">
                          <EditableSelect
                            valueToRender={facility.environment || account.environment}
                            editMode={rowEditMode}
                            value={facility.environment || account.environment}
                            onChange={onChangeFacility(facilityId, 'environment')}
                          >
                            {utahIcsEnvironmentOptions.map((env) => (
                              <MenuItem key={env.value} value={env.value}>
                                {env.label}
                              </MenuItem>
                            ))}
                          </EditableSelect>
                        </TableCell>
                        <TableCell className="cell-wrap-word">
                          <EditableTextField
                            valueToRender={facility.facilityCode}
                            editMode={rowEditMode}
                            value={facility.facilityCode}
                            onChange={onChangeFacility(facilityId, 'facilityCode')}
                            type="text"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </TableCell>
                        <TableCell className="cell-wrap-word">
                          <EditableTextField
                            valueToRender={facility.clientId || account.clientId}
                            editMode={rowEditMode}
                            value={facility.clientId || account.clientId}
                            onChange={onChangeFacility(facilityId, 'clientId')}
                            type="text"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </TableCell>
                        <TableCell className="cell-wrap-word">
                          <EditableTextField
                            valueToRender={
                              rowEditMode
                                ? facility.clientSecret || account.clientSecret
                                : facility.clientSecret || account.clientSecret
                                ? '******'
                                : ''
                            }
                            editMode={rowEditMode}
                            value={facility.clientSecret || account.clientSecret}
                            onChange={onChangeFacility(facilityId, 'clientSecret')}
                            type="text"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <EditableSwitch
                            valueToRender={facility.enabled ? 'Enabled' : 'Disabled'}
                            checked={facility.enabled || false}
                            editMode={rowEditMode}
                            onChange={onChangeFacility(facilityId, 'enabled', true)}
                          />
                        </TableCell>
                        <TableCell>
                          <EditableSwitch
                            valueToRender={facility.enableSync ? 'Enabled' : 'Disabled'}
                            checked={facility.enableSync || false}
                            editMode={rowEditMode}
                            onChange={onChangeFacility(facilityId, 'enableSync', true)}
                          />
                        </TableCell>
                        <TableCell>
                          <Grid container spacing={0} alignItems="center">
                            {rowEditMode ? (
                              <Grid item>
                                <Button
                                  className="primary-button"
                                  onClick={() => {
                                    const result = onSaveFacility(facilityId)
                                    if (result && typeof result.then === 'function') {
                                      result
                                        .then(() => toggleEdit(facilityId, false))
                                        .catch(() => {})
                                    }
                                  }}
                                  aria-label="save-facility"
                                >
                                  Save
                                </Button>
                              </Grid>
                            ) : (
                              <Grid item>
                                <Button
                                  className="primary-button"
                                  onClick={() => toggleEdit(facilityId, true)}
                                  aria-label="edit-facility"
                                >
                                  Edit
                                </Button>
                              </Grid>
                            )}
                            <Grid item>
                              {rowEditMode ? (
                                <Button
                                  className="danger-button"
                                  onClick={() => toggleEdit(facilityId, false)}
                                  aria-label="cancel-edit-facility"
                                  style={{ marginLeft: 8 }}
                                >
                                  Cancel
                                </Button>
                              ) : (
                                <Button
                                  className="danger-button"
                                  onClick={() => onRemoveFacility(facilityId)}
                                  aria-label="remove-facility"
                                  style={{ marginLeft: 8 }}
                                >
                                  Delete
                                </Button>
                              )}
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                {availableShops &&
                  availableShops.map((shop, index) => (
                    <TableRow key={index}>
                      <TableCell>{shop.name}</TableCell>
                      <TableCell colSpan={7}>
                        <Button
                          className="primary-button"
                          onClick={() =>
                            onAddFacility({
                              shopId: shop.id,
                              companyId: account.companyId,
                            })
                          }
                        >
                          Add Facility
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default UtahIcsFacilities



// WEBPACK FOOTER //
// ./src/components/Compliance/UtahIcsFacilities.js